import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

function Opportunities() {
  const [data, setData] = useState({
    ending_packages: [],
    churn_risk: [],
    birthdays: [],
    no_shows: [],
    expired_quotes: [],
    unconverted_consults: [],
    revisit_due: [],
    debtors: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('packages'); // 'packages' | 'churn' | 'birthdays' | 'no_shows' | 'quotes' | 'consults' | 'revisit' | 'debtors'
  
  // Bulk select states
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Payment link modal states
  const [showPayLinkModal, setShowPayLinkModal] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [currentDebtor, setCurrentDebtor] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);

  useEffect(() => {
    fetchOpportunities();
  }, []);

  useEffect(() => {
    // Clear selection when changing tabs
    setSelectedIds([]);
  }, [activeTab]);

  const fetchOpportunities = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const response = await axios.get('/api/opportunities', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData({
        ending_packages: response.data.ending_packages || [],
        churn_risk: response.data.churn_risk || [],
        birthdays: response.data.birthdays || [],
        no_shows: response.data.no_shows || [],
        expired_quotes: response.data.expired_quotes || [],
        unconverted_consults: response.data.unconverted_consults || [],
        revisit_due: response.data.revisit_due || [],
        debtors: response.data.debtors || []
      });
    } catch (error) {
      console.error('Opportunities fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActiveList = () => {
    switch (activeTab) {
      case 'packages': return data.ending_packages;
      case 'churn': return data.churn_risk;
      case 'birthdays': return data.birthdays;
      case 'no_shows': return data.no_shows;
      case 'quotes': return data.expired_quotes;
      case 'consults': return data.unconverted_consults;
      case 'revisit': return data.revisit_due;
      case 'debtors': return data.debtors;
      default: return [];
    }
  };

  const getTierBadge = (tier) => {
    if (!tier) return null;
    const colors = {
      bronze: { bg: 'rgba(217, 119, 6, 0.15)', color: '#f59e0b', border: '1px solid rgba(217, 119, 6, 0.3)' },
      silver: { bg: 'rgba(148, 163, 184, 0.15)', color: '#cbd5e1', border: '1px solid rgba(148, 163, 184, 0.3)' },
      gold: { bg: 'rgba(234, 179, 8, 0.15)', color: '#facc15', border: '1px solid rgba(234, 179, 8, 0.3)' },
      platinum: { bg: 'rgba(0, 242, 254, 0.15)', color: '#00f2fe', border: '1px solid rgba(0, 242, 254, 0.3)' }
    };
    const style = colors[tier] || colors.bronze;
    return (
      <span style={{ 
        background: style.bg, 
        color: style.color,
        border: style.border,
        padding: '3px 8px', 
        borderRadius: '6px', 
        fontSize: '10px',
        fontWeight: 600,
        textTransform: 'uppercase'
      }}>
        {tier}
      </span>
    );
  };

  const getWhatsAppMessage = (name, type, extra = {}) => {
    switch (type) {
      case 'packages':
        return `Merhaba ${name}, satın almış olduğunuz hizmet paketinizdeki seanslarınız tamamlanmak üzere. Size yeni paket kampanyalarımız hakkında bilgi vermek isteriz.`;
      case 'churn':
        return `Merhaba ${name}, sizi merkezimizde görmeyeli uzun zaman oldu. Sizi özledik! Bu aya özel size tanımladığımız indirimli hizmetlerimizden yararlanmak ister misiniz?`;
      case 'birthdays':
        return `Merhaba ${name}, doğum gününüz kutlu olsun! 🎉 Doğum gününüze özel seanslarınızda geçerli %20 indirim tanımladık. Mutlu yıllar dileriz!`;
      case 'no_shows':
        return `Merhaba ${name}, randevunuza gelemediğinizi fark ettik. Sizin için yeni bir randevu oluşturalım mı?`;
      case 'quotes':
        return `Merhaba ${name}, size özel hazırladığımız teklifimizin geçerlilik süresi doldu. Teklifi güncellemek veya yardımcı olabileceğimiz bir konu var mı?`;
      case 'consults':
        return `Merhaba ${name}, yaptığımız ön görüşme sonrasında aklınıza takılan bir soru oldu mu? Size en uygun çözümü planlamak için buradayız.`;
      case 'revisit':
        return `Merhaba ${name}, son işleminizin üzerinden zaman geçti. Devam seansınız veya kontrolünüz için uygun bir gün planlayalım mı?`;
      case 'debtors':
        return `Merhaba ${name}, açıkta bulunan ₺${extra.debt || 0} tutarındaki ödemenizi aşağıdaki güvenli ödeme bağlantısı üzerinden tamamlayabilirsiniz: ${extra.link || ''}`;
      default:
        return '';
    }
  };

  const openWhatsApp = (phone, name, type, extra = {}) => {
    let cleanPhone = phone.replace(/[^0-9]/g, '');
    if (cleanPhone.startsWith('0')) cleanPhone = '90' + cleanPhone.substring(1);
    if (!cleanPhone.startsWith('90') && cleanPhone.length === 10) cleanPhone = '90' + cleanPhone;

    const text = getWhatsAppMessage(name, type, extra);
    const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const handleBulkWhatsApp = async () => {
    const list = getActiveList();
    const selectedUsers = list.filter(item => selectedIds.includes(item.customer_id));
    if (selectedUsers.length === 0) return;

    // Open one blank tab per recipient synchronously (within the click handler) so the
    // popup blocker allows them, then fill in each tab's URL once the message/link is ready.
    const windows = selectedUsers.map(() => window.open('', '_blank'));

    for (let i = 0; i < selectedUsers.length; i++) {
      const user = selectedUsers[i];
      let cleanPhone = user.phone.replace(/[^0-9]/g, '');
      if (cleanPhone.startsWith('0')) cleanPhone = '90' + cleanPhone.substring(1);
      if (!cleanPhone.startsWith('90') && cleanPhone.length === 10) cleanPhone = '90' + cleanPhone;

      let extra = {};
      if (activeTab === 'debtors') {
        let link = '';
        try {
          const token = localStorage.getItem('crm_token');
          const res = await axios.post(`/api/payments/${user.payment_id}/payment-link`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          link = res.data.payment_link;
        } catch (e) {
          console.error('Payment link error:', e);
        }
        extra = { debt: user.total_debt, link };
      }

      const text = getWhatsAppMessage(`${user.first_name} ${user.last_name || ''}`, activeTab, extra);
      const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`;
      if (windows[i]) windows[i].location.href = url;
    }
  };

  // Generate Payment Link
  const handleCreatePaymentLink = async (debtor) => {
    setCurrentDebtor(debtor);
    setLinkLoading(true);
    setShowPayLinkModal(true);
    setGeneratedLink('');
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.post(`/api/payments/${debtor.payment_id}/payment-link`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedLink(res.data.payment_link);
    } catch (e) {
      console.error(e);
      alert('Ödeme linki oluşturulamadı, lütfen tekrar deneyin.');
      setShowPayLinkModal(false);
    } finally {
      setLinkLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedLink);
    alert('Ödeme linki panoya kopyalandı.');
  };

  const shareLinkWhatsApp = () => {
    if (!currentDebtor) return;
    openWhatsApp(currentDebtor.phone, `${currentDebtor.first_name} ${currentDebtor.last_name || ''}`, 'debtors', {
      debt: currentDebtor.total_debt,
      link: generatedLink
    });
  };

  const toggleSelectAll = () => {
    const list = getActiveList();
    if (selectedIds.length === list.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(list.map(item => item.customer_id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(prev => prev.filter(x => x !== id));
    } else {
      setSelectedIds(prev => [...prev, id]);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const activeList = getActiveList();

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Para Kazandıracak Fırsatlar</h1>
          <p className="page-subtitle">Şubenizde gelir artırıcı müşteri aksiyonları ve teklif fırsatları</p>
        </div>
        <Link to="/" className="btn btn-secondary">
          ← Dashboard'a Dön
        </Link>
      </div>

      {/* Grid Summary Cards (8 Segments) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {[
          { key: 'packages', label: 'Paketi Bitmek Üzere', count: data.ending_packages.length, icon: '🎗️', desc: 'Kalan seansı az olanlar', color: 'var(--primary)' },
          { key: 'churn', label: '90+ Gündür Gelmeyen', count: data.churn_risk.length, icon: '⏳', desc: 'Churn riski taşıyanlar', color: 'var(--secondary)' },
          { key: 'birthdays', label: 'Bu Hafta Doğum Günü', count: data.birthdays.length, icon: '🎂', desc: 'Kutlama ve kampanya', color: '#fbbf24' },
          { key: 'no_shows', label: 'Gelmeyenler (30 Gün)', count: data.no_shows.length, icon: '⚠️', desc: 'Randevuya gelmeyenler', color: '#f43f5e' },
          { key: 'quotes', label: 'Süresi Geçen Teklifler', count: data.expired_quotes.length, icon: '📄', desc: 'Teklif alıp almayanlar', color: '#10b981' },
          { key: 'consults', label: 'Ön Görüşme - Dönüşmeyen', count: data.unconverted_consults.length, icon: '💬', desc: 'Aranmamış ön görüşmeler', color: '#a78bfa' },
          { key: 'revisit', label: 'Tekrar İşlem Zamanı', count: data.revisit_due.length, icon: '🔄', desc: 'Ziyaret aralığı dolanlar', color: '#00f2fe' },
          { key: 'debtors', label: 'Borcu Olan Müşteriler', count: data.debtors.length, icon: '💰', desc: 'Ödeme bekleyen alacaklar', color: '#ec4899' }
        ].map((card) => (
          <div 
            key={card.key}
            className="stat-card cursor-pointer" 
            onClick={() => setActiveTab(card.key)}
            style={{ 
              borderColor: activeTab === card.key ? card.color : 'var(--border)',
              boxShadow: activeTab === card.key ? `0 0 15px ${card.color}25` : 'none',
              transform: activeTab === card.key ? 'translateY(-2px)' : 'none',
              transition: 'all .2s'
            }}
          >
            <div className="stat-icon" style={{ background: `${card.color}15`, color: card.color }}>{card.icon}</div>
            <div className="stat-content">
              <div className="stat-label">{card.label}</div>
              <div className="stat-value" style={{ color: card.color }}>{card.count}</div>
              <div className="stat-change" style={{ color: 'var(--text-muted)' }}>{card.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Action Bar for Bulk Selection */}
      {selectedIds.length > 0 && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(30, 30, 40, 0.95)', border: '1px solid var(--primary)', borderRadius: '16px',
          padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '20px', zIndex: 99,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)', backdropFilter: 'blur(10px)'
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>
            ⚡ {selectedIds.length} Müşteri Seçildi
          </span>
          <button className="btn btn-success btn-sm" onClick={handleBulkWhatsApp}>
            💬 WhatsApp Toplu Şablon
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelectedIds([])}>
            İptal
          </button>
        </div>
      )}

      {/* Table Card Wrapper */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '0 4px' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>
            {(() => {
              const titles = {
                packages: '🎗️ Paketi Bitmek Üzere olanlar',
                churn: '⏳ 90+ Gündür Gelmeyenler',
                birthdays: '🎂 Bu Hafta Doğum Günü olanlar',
                no_shows: '⚠️ Randevu Alıp Gelmeyenler',
                quotes: '📄 Süresi Geçen Teklifler',
                consults: '💬 Dönüşmeyen Ön Görüşme İstekleri',
                revisit: '🔄 Tekrar Ziyaret Hatırlatma Zamanı Gelenler',
                debtors: '💰 Borcu Bulunan Müşteriler'
              };
              return titles[activeTab];
            })()}
          </h3>
          {activeList.length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={toggleSelectAll}>
              {selectedIds.length === activeList.length ? 'Seçimleri Kaldır' : 'Tümünü Seç'}
            </button>
          )}
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              {activeTab === 'packages' && (
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Müşteri</th>
                  <th>İletişim</th>
                  <th>Hizmet Paketi</th>
                  <th style={{ textAlign: 'center' }}>Kalan Seans</th>
                  <th style={{ width: '220px' }}>Aksiyonlar</th>
                </tr>
              )}
              {activeTab === 'churn' && (
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Müşteri</th>
                  <th>İletişim</th>
                  <th>Üyelik</th>
                  <th>Son Ziyaret</th>
                  <th style={{ width: '220px' }}>Aksiyonlar</th>
                </tr>
              )}
              {activeTab === 'birthdays' && (
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Müşteri</th>
                  <th>İletişim</th>
                  <th>Üyelik</th>
                  <th>Doğum Tarihi</th>
                  <th style={{ width: '220px' }}>Aksiyonlar</th>
                </tr>
              )}
              {activeTab === 'no_shows' && (
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Müşteri</th>
                  <th>İletişim</th>
                  <th>Randevu Tarihi</th>
                  <th>Kayıp Tutar</th>
                  <th style={{ width: '220px' }}>Aksiyonlar</th>
                </tr>
              )}
              {activeTab === 'quotes' && (
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Müşteri</th>
                  <th>İletişim</th>
                  <th>Teklif No</th>
                  <th>Teklif Tutarı</th>
                  <th>Geçerlilik</th>
                  <th style={{ width: '220px' }}>Aksiyonlar</th>
                </tr>
              )}
              {activeTab === 'consults' && (
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Müşteri</th>
                  <th>İletişim</th>
                  <th>Görüşme Tarihi</th>
                  <th>Açıklama</th>
                  <th style={{ width: '220px' }}>Aksiyonlar</th>
                </tr>
              )}
              {activeTab === 'revisit' && (
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Müşteri</th>
                  <th>İletişim</th>
                  <th>Son Ziyaret</th>
                  <th>Ort. Ziyaret Aralığı</th>
                  <th style={{ width: '220px' }}>Aksiyonlar</th>
                </tr>
              )}
              {activeTab === 'debtors' && (
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Müşteri</th>
                  <th>İletişim</th>
                  <th>Açık Fatura Sayısı</th>
                  <th>Toplam Borç</th>
                  <th style={{ width: '260px' }}>Aksiyonlar</th>
                </tr>
              )}
            </thead>
            <tbody>
              {activeList.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center text-muted" style={{ padding: '32px' }}>
                    Kayıt bulunmuyor.
                  </td>
                </tr>
              ) : (
                activeList.map((item, idx) => (
                  <tr key={idx} style={{ background: selectedIds.includes(item.customer_id) ? 'rgba(var(--primary-rgb), 0.04)' : 'transparent' }}>
                    <td>
                      <input 
                        type="checkbox" 
                        checked={selectedIds.includes(item.customer_id)}
                        onChange={() => toggleSelect(item.customer_id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td>
                      <Link to={`/customers/${item.customer_id}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }}>
                        <div className="avatar" style={{ background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)' }}>
                          {item.first_name?.[0] || '?'}{item.last_name?.[0] || ''}
                        </div>
                        <div>
                          <div className="font-medium" style={{ color: 'var(--text)' }}>
                            {item.first_name} {item.last_name}
                          </div>
                          <div className="text-xs text-muted">{item.email || '-'}</div>
                        </div>
                      </Link>
                    </td>
                    <td>
                      <div className="text-sm">{item.phone}</div>
                    </td>

                    {/* Specific column rendering */}
                    {activeTab === 'packages' && (
                      <>
                        <td>
                          <div className="font-semibold" style={{ color: 'var(--text)' }}>{item.package_name}</div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="badge badge-danger" style={{ fontSize: '11px', padding: '4px 10px' }}>
                            {item.remaining_sessions} / {item.total_sessions}
                          </span>
                        </td>
                      </>
                    )}

                    {activeTab === 'churn' && (
                      <>
                        <td>{getTierBadge(item.loyalty_tier)}</td>
                        <td>
                          <div className="text-sm">
                            {new Date(item.last_visit_date).toLocaleDateString('tr-TR')} 
                            <span style={{ color: 'var(--danger)', fontSize: '11px', marginLeft: '6px', fontWeight: 600 }}>
                              ({Math.floor((new Date() - new Date(item.last_visit_date)) / (1000 * 60 * 60 * 24))} gün önce)
                            </span>
                          </div>
                        </td>
                      </>
                    )}

                    {activeTab === 'birthdays' && (
                      <>
                        <td>{getTierBadge(item.loyalty_tier)}</td>
                        <td>
                          <div className="text-sm font-semibold" style={{ color: '#fbbf24' }}>
                            🎂 {new Date(item.birth_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })}
                          </div>
                        </td>
                      </>
                    )}

                    {activeTab === 'no_shows' && (
                      <>
                        <td>
                          <div className="text-sm">{new Date(item.scheduled_at).toLocaleDateString('tr-TR')} {new Date(item.scheduled_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
                        </td>
                        <td>
                          <div className="text-sm font-semibold" style={{ color: 'var(--danger)' }}>{item.final_price || '0.00'} ₺</div>
                        </td>
                      </>
                    )}

                    {activeTab === 'quotes' && (
                      <>
                        <td>
                          <div className="text-sm font-medium">{item.quote_number}</div>
                        </td>
                        <td>
                          <div className="text-sm font-semibold" style={{ color: 'var(--success)' }}>{item.total_amount} ₺</div>
                        </td>
                        <td>
                          <div className="text-sm text-muted">{new Date(item.valid_until).toLocaleDateString('tr-TR')} (Geçti)</div>
                        </td>
                      </>
                    )}

                    {activeTab === 'consults' && (
                      <>
                        <td>
                          <div className="text-sm">{new Date(item.created_at).toLocaleDateString('tr-TR')}</div>
                        </td>
                        <td>
                          <div className="text-sm text-muted" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.notes}>{item.notes || '-'}</div>
                        </td>
                      </>
                    )}

                    {activeTab === 'revisit' && (
                      <>
                        <td>
                          <div className="text-sm">{new Date(item.last_visit_date).toLocaleDateString('tr-TR')}</div>
                        </td>
                        <td>
                          <div className="text-sm font-medium">{item.average_visit_gap} gün</div>
                        </td>
                      </>
                    )}

                    {activeTab === 'debtors' && (
                      <>
                        <td>
                          <div className="text-sm font-medium">{item.unpaid_count} açık ödeme</div>
                        </td>
                        <td>
                          <div className="text-sm font-bold" style={{ color: '#ec4899' }}>{Number(item.total_debt).toLocaleString('tr-TR')} ₺</div>
                        </td>
                      </>
                    )}

                    <td>
                      <div className="flex gap-2">
                        <Link to={`/customers/${item.customer_id}`} className="btn btn-secondary btn-sm">
                          🔍 İncele
                        </Link>
                        {activeTab === 'debtors' ? (
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => handleCreatePaymentLink(item)}
                          >
                            🔗 Link Üret
                          </button>
                        ) : (
                          <button 
                            className="btn btn-success btn-sm"
                            onClick={() => openWhatsApp(item.phone, `${item.first_name} ${item.last_name || ''}`, activeTab)}
                          >
                            💬 WhatsApp
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment Link Modal */}
      {showPayLinkModal && (
        <div className="modal-overlay" onClick={() => setShowPayLinkModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Ödeme Bağlantısı Oluşturuldu</h3>
              <button className="modal-close" onClick={() => setShowPayLinkModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: '24px 16px' }}>
              {linkLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                  <div className="loading-spinner"></div>
                  <p className="text-muted">Ödeme linki hazırlanıyor...</p>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔗</div>
                  <h4 style={{ margin: '0 0 8px', color: '#fff' }}>{currentDebtor?.first_name} {currentDebtor?.last_name}</h4>
                  <p style={{ color: '#ec4899', fontWeight: 700, fontSize: '18px', margin: '0 0 16px' }}>₺{Number(currentDebtor?.total_debt).toLocaleString('tr-TR')}</p>
                  
                  <div style={{ 
                    background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)', 
                    borderRadius: '12px', padding: '12px', wordBreak: 'break-all', 
                    fontSize: '12px', color: 'var(--primary)', fontWeight: 600, marginBottom: '24px' 
                  }}>
                    {generatedLink}
                  </div>

                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    <button className="btn btn-secondary" onClick={copyToClipboard}>
                      📋 Linki Kopyala
                    </button>
                    <button className="btn btn-success" onClick={shareLinkWhatsApp}>
                      💬 WhatsApp ile Gönder
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Opportunities;
